import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import {
  ChevronDown, ChevronRight, Pencil, Lock, FileText, AlertTriangle, RefreshCw, Loader2,
} from "lucide-react";

const STATUS = {
  draft: { bg: "#3B82F6", fg: "#93C5FD", label: "Draft" },
  completed: { bg: "#F59E0B", fg: "#FCD34D", label: "Completed" },
  signed: { bg: "#22C55E", fg: "#86EFAC", label: "Signed" },
  locked: { bg: "#64748B", fg: "#E2E8F0", label: "Locked" },
  corrected: { bg: "#8B5CF6", fg: "#C4B5FD", label: "Addendum" },
  cancelled: { bg: "#6B7280", fg: "#D1D5DB", label: "Cancelled" },
};

// Human-readable section titles; anything else falls back to Title Case.
const SECTION_TITLES = {
  intake: "Intake & history", subjective: "Subjective (S)", objective: "Objective (O)",
  clinical_impression: "Assessment / clinical impression (A)", treatment: "Treatment provided",
  plan: "Plan (P)", followup: "Follow-up", reassessment: "Reassessment", discharge: "Discharge",
};
const TABLE_SECTIONS = { rom: "Range of motion", strength: "Strength", functional: "Functional tests", outcome_measures: "Outcome measures" };
const OBJECT_SECTIONS = ["intake", "subjective", "objective", "clinical_impression", "treatment", "plan", "followup", "reassessment", "discharge"];

const titleCase = (s) => String(s).replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
const fmtDate = (a) => {
  const d = a.appointment_scheduled_at || a.completed_at || a.signed_at || a.created_at;
  try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return (d || "").slice(0, 10); }
};
const isEmptyVal = (v) =>
  v === null || v === undefined || v === "" ||
  (Array.isArray(v) && v.length === 0) ||
  (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0);

function StatusPill({ status }) {
  const c = STATUS[status] || STATUS.draft;
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: `${c.bg}22`, color: c.fg, border: `1px solid ${c.bg}66` }}
      data-testid={`pan-status-${status}`}>
      {status === "locked" && <Lock size={10} />} {c.label}
    </span>
  );
}

function KeyVals({ obj }) {
  const scalarArr = (v) => Array.isArray(v) && v.every((x) => x === null || typeof x !== "object");
  const entries = Object.entries(obj || {}).filter(
    ([k, v]) => !k.startsWith("_") && !isEmptyVal(v) && (typeof v !== "object" || scalarArr(v))
  );
  if (!entries.length) return null;
  return (
    <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2">
      {entries.map(([k, v]) => (
        <div key={k}>
          <dt className="text-[11px] uppercase tracking-wide" style={{ color: "var(--fl-muted-2)" }}>{titleCase(k)}</dt>
          <dd className="text-sm whitespace-pre-wrap" style={{ color: "var(--fl-text)" }}>{Array.isArray(v) ? v.join(", ") : String(v)}</dd>
        </div>
      ))}
    </dl>
  );
}

function RowsTable({ rows }) {
  const cols = useMemo(() => {
    const keys = new Set();
    (rows || []).forEach((r) => Object.keys(r).forEach((k) => { if (k !== "id" && !isEmptyVal(r[k])) keys.add(k); }));
    return [...keys];
  }, [rows]);
  if (!rows?.length) return null;
  return (
    <div className="overflow-x-auto rounded-md border" style={{ borderColor: "var(--fl-border)" }}>
      <table className="w-full text-sm">
        <thead>
          <tr style={{ background: "var(--fl-bg-3, rgba(255,255,255,0.04))" }}>
            {cols.map((c) => <th key={c} className="text-left px-3 py-1.5 text-[11px] uppercase tracking-wide" style={{ color: "var(--fl-muted-2)" }}>{titleCase(c)}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t" style={{ borderColor: "var(--fl-border)" }}>
              {cols.map((c) => <td key={c} className="px-3 py-1.5" style={{ color: "var(--fl-text)" }}>{isEmptyVal(r[c]) ? "—" : String(r[c])}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function SectionBlock({ title, children }) {
  return (
    <div>
      <div className="fl-heading text-sm mb-2" style={{ color: "var(--fl-orange)" }}>{title}</div>
      {children}
    </div>
  );
}

function NoteContent({ a }) {
  const blocks = [];
  for (const key of OBJECT_SECTIONS) {
    const obj = a[key];
    if (obj && Object.values(obj).some((v) => !isEmptyVal(v) && typeof v !== "object")) {
      blocks.push(<SectionBlock key={key} title={SECTION_TITLES[key] || titleCase(key)}><KeyVals obj={obj} /></SectionBlock>);
    }
  }
  for (const key of Object.keys(TABLE_SECTIONS)) {
    if (a[key]?.length) blocks.push(<SectionBlock key={key} title={TABLE_SECTIONS[key]}><RowsTable rows={a[key]} /></SectionBlock>);
  }
  return (
    <div className="space-y-5">
      {blocks.length ? blocks : <div className="text-sm" style={{ color: "var(--fl-muted)" }}>This record has no saved clinical content yet.</div>}
      {(a.addenda?.length > 0 || a.amendments?.length > 0) && (
        <SectionBlock title={`Addenda & corrections (${(a.addenda?.length || 0) + (a.amendments?.length || 0)})`}>
          <div className="space-y-3">
            {(a.addenda || []).map((ad, i) => (
              <div key={`ad-${i}`} className="rounded-md p-3" style={{ background: "color-mix(in oklab, #8B5CF6 8%, transparent)", border: "1px solid #8B5CF655" }} data-testid={`pan-addendum-${a.id}-${i}`}>
                <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                  {ad.at ? new Date(ad.at).toLocaleString("en-GB") : ""} · {ad.author_name || ad.author || ""}
                </div>
                {ad.reason && <div className="text-sm mt-1"><b>Reason:</b> {ad.reason}</div>}
                {(ad.text || ad.content) && <div className="text-sm mt-1 whitespace-pre-wrap">{ad.text || ad.content}</div>}
              </div>
            ))}
            {(a.amendments || []).map((am, i) => (
              <div key={`am-${i}`} className="rounded-md p-3" style={{ background: "color-mix(in oklab, #8B5CF6 8%, transparent)", border: "1px solid #8B5CF655" }} data-testid={`pan-amendment-${a.id}-${i}`}>
                <div className="text-[11px]" style={{ color: "var(--fl-muted-2)" }}>
                  {am.at || am.created_at ? new Date(am.at || am.created_at).toLocaleString("en-GB") : ""} · {am.signed_by_name || am.author_name || am.clinician_name || am.author || ""}{am.signed_by_role ? ` (${am.signed_by_role})` : ""}
                </div>
                {am.reason && <div className="text-sm mt-1"><b>Reason:</b> {am.reason}</div>}
                {(am.text || am.content || am.note) && <div className="text-sm mt-1 whitespace-pre-wrap">{am.text || am.content || am.note}</div>}
                {am.fields_changed?.length > 0 && <div className="text-xs mt-1" style={{ color: "var(--fl-muted)" }}>Fields changed: {am.fields_changed.map((f) => titleCase(String(f).replace(/\./g, " › "))).join(", ")}</div>}
              </div>
            ))}
          </div>
        </SectionBlock>
      )}
    </div>
  );
}

function NoteCard({ a, open, onToggle, patientId }) {
  const status = a.status || "draft";
  const isDraft = status === "draft" || status === "completed";
  return (
    <div className="fl-card fl-card-elevated overflow-hidden" data-testid={`pan-card-${a.id}`}>
      <button type="button" onClick={() => onToggle(a.id)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors"
        aria-expanded={open} data-testid={`pan-toggle-${a.id}`}>
        {open ? <ChevronDown size={18} className="shrink-0" style={{ color: "var(--fl-orange)" }} />
              : <ChevronRight size={18} className="shrink-0" style={{ color: "var(--fl-muted)" }} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="fl-heading text-base" style={{ color: "var(--fl-text)" }}>{fmtDate(a)}</span>
            <span className="text-sm" style={{ color: "var(--fl-muted)" }}>· {titleCase(a.assessment_type || "assessment")}</span>
            <StatusPill status={status} />
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{a.clinician_name || "—"}{a.service ? ` · ${a.service}` : ""}</div>
        </div>
        <span className="text-[11px] shrink-0 hidden sm:inline" style={{ color: "var(--fl-muted)" }}>
          {open ? "Collapse note" : "Open note"}
        </span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t" style={{ borderColor: "var(--fl-border)" }} data-testid={`pan-body-${a.id}`}>
          <NoteContent a={a} />
          <div className="mt-4 flex items-center gap-2">
            {isDraft ? (
              <Link to={`/clinician/notes?patient=${patientId}&assessment=${a.id}`}
                className="fl-btn text-xs inline-flex items-center gap-1.5" data-testid={`pan-edit-${a.id}`}>
                <Pencil size={12} /> Edit draft
              </Link>
            ) : (
              <Link to={`/clinician/notes?patient=${patientId}&assessment=${a.id}`}
                className="fl-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid={`pan-open-workspace-${a.id}`}>
                <FileText size={12} /> Open in workspace{["locked", "corrected", "signed"].includes(status) ? " (amend)" : ""}
              </Link>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function LegacyCard({ n, open, onToggle }) {
  return (
    <div className="fl-card overflow-hidden" data-testid={`pan-legacy-${n.id}`}>
      <button type="button" onClick={() => onToggle(`legacy-${n.id}`)}
        className="w-full flex items-center gap-3 p-4 text-left hover:bg-white/5 transition-colors" aria-expanded={open}>
        {open ? <ChevronDown size={18} style={{ color: "var(--fl-orange)" }} /> : <ChevronRight size={18} style={{ color: "var(--fl-muted)" }} />}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="fl-heading text-base">{new Date(n.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span>
            <span className="text-sm" style={{ color: "var(--fl-muted)" }}>· Quick note (legacy)</span>
            <StatusPill status="locked" />
          </div>
          <div className="text-xs mt-0.5" style={{ color: "var(--fl-muted-2)" }}>{n.clinician_name || "—"}</div>
        </div>
        <span className="text-[11px] hidden sm:inline" style={{ color: "var(--fl-muted)" }}>{open ? "Collapse note" : "Open note"}</span>
      </button>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t space-y-2" style={{ borderColor: "var(--fl-border)" }}>
          {n.subjective && <div className="text-sm"><span style={{ color: "var(--fl-orange)" }}>S:</span> {n.subjective}</div>}
          {n.objective && <div className="text-sm"><span style={{ color: "var(--fl-orange)" }}>O:</span> {n.objective}</div>}
          {n.assessment && <div className="text-sm"><span style={{ color: "var(--fl-orange)" }}>A:</span> {n.assessment}</div>}
          {n.plan && <div className="text-sm"><span style={{ color: "var(--fl-orange)" }}>P:</span> {n.plan}</div>}
          {!n.subjective && !n.objective && !n.assessment && !n.plan && <div className="text-sm" style={{ color: "var(--fl-muted)" }}>No content.</div>}
        </div>
      )}
    </div>
  );
}

export default function PatientAssessmentNotes({ patientId, legacyNotes = [] }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openIds, setOpenIds] = useState(() => new Set());

  const load = useCallback(() => {
    setLoading(true); setError(null);
    api.get(`/assessments/patient/${patientId}`)
      .then((r) => setList(Array.isArray(r.data) ? r.data : []))
      .catch(() => setError("Could not load this patient's notes."))
      .finally(() => setLoading(false));
  }, [patientId]);

  useEffect(() => { load(); }, [load]);

  const toggle = useCallback((idKey) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      next.has(idKey) ? next.delete(idKey) : next.add(idKey);
      return next;
    });
  }, []);

  const allKeys = useMemo(
    () => [...list.map((a) => a.id), ...legacyNotes.map((n) => `legacy-${n.id}`)],
    [list, legacyNotes]
  );
  const openAll = () => setOpenIds(new Set(allKeys));
  const collapseAll = () => setOpenIds(new Set());
  const openCount = openIds.size;

  return (
    <div className="fl-card fl-card-elevated p-5" data-testid="patient-assessment-notes">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="fl-heading text-xl">Assessments &amp; Notes</div>
          <div className="text-xs" style={{ color: "var(--fl-muted)" }}>Read-only history · newest first. {list.length + legacyNotes.length} record(s).</div>
        </div>
        <div className="flex items-center gap-2">
          <Link to={`/clinician/notes?patient=${patientId}&walkin=1`} className="fl-btn text-xs inline-flex items-center gap-1.5" data-testid="pan-new-note">New note</Link>
          <button type="button" onClick={openAll} className="fl-btn-ghost text-xs" data-testid="pan-open-all" disabled={!allKeys.length}>Open all notes</button>
          <button type="button" onClick={collapseAll} className="fl-btn-ghost text-xs" data-testid="pan-collapse-all" disabled={!openCount}>Collapse all notes</button>
        </div>
      </div>

      {loading && <div className="flex items-center gap-2 text-sm py-6" style={{ color: "var(--fl-muted)" }}><Loader2 size={16} className="animate-spin" /> Loading notes…</div>}

      {error && !loading && (
        <div className="flex items-center justify-between gap-3 rounded-md p-3" style={{ background: "color-mix(in oklab, #ef4444 8%, transparent)", border: "1px solid #ef444455" }} data-testid="pan-error">
          <div className="flex items-center gap-2 text-sm" style={{ color: "#fca5a5" }}><AlertTriangle size={15} /> {error}</div>
          <button type="button" onClick={load} className="fl-btn-ghost text-xs inline-flex items-center gap-1.5" data-testid="pan-retry"><RefreshCw size={12} /> Retry</button>
        </div>
      )}

      {!loading && !error && (
        <div className="space-y-3" data-testid="pan-list">
          {list.length === 0 && legacyNotes.length === 0 && (
            <div className="text-sm py-6" style={{ color: "var(--fl-muted)" }}>No notes yet for this patient.</div>
          )}
          {list.map((a) => (
            <NoteCard key={a.id} a={a} open={openIds.has(a.id)} onToggle={toggle} patientId={patientId} />
          ))}
          {legacyNotes.length > 0 && (
            <>
              <div className="text-[11px] uppercase tracking-wide pt-2" style={{ color: "var(--fl-muted-2)" }}>Earlier quick notes (legacy)</div>
              {legacyNotes.map((n) => (
                <LegacyCard key={n.id} n={n} open={openIds.has(`legacy-${n.id}`)} onToggle={toggle} />
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
